import { Component, inject } from '@angular/core';
import {
  BehaviorSubject,
  forkJoin,
  Observable,
  of,
  shareReplay,
  Subscription,
} from 'rxjs';
import { GpsOnlyService } from '../../services/gps-only.service';
import { ActivatedRoute } from '@angular/router';
import { GpsOnly } from '../../models/gps-only.model';
import { AsyncPipe } from '@angular/common';
import {
  Coordinate,
  GpsPlayerComponent,
  Top3,
  TrainingSection,
  User,
} from '@kinomap-dev/ng-ui-lib';
import { TranslateModule } from '@ngx-translate/core';
import { WatchActionsService } from 'app/services/watch-actions.service';
import { Comment } from '@kinomap-dev/ng-ui-lib/lib/models/comment.model';
import { GenericViewComponent } from '../../components/generic-view/generic-view.component';
import { ActivityApi } from 'app/modules/dashboard/api/activity.api';

@Component({
  standalone: true,
  styleUrl: './gps-only.component.scss',
  templateUrl: './gps-only.component.html',
  imports: [
    AsyncPipe,
    TranslateModule,
    GpsPlayerComponent,
    GenericViewComponent,
  ],
})
export class GpsOnlyPageComponent {
  private gpsOnlyService: GpsOnlyService = inject(GpsOnlyService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private activityApi: ActivityApi = inject(ActivityApi);
  public watchActionsService: WatchActionsService = inject(WatchActionsService);

  information$: Observable<GpsOnly | null>;
  coordinates$: BehaviorSubject<Coordinate[]> = new BehaviorSubject<
    Coordinate[]
  >([]);
  trainingSections$: BehaviorSubject<TrainingSection[]> = new BehaviorSubject<
    TrainingSection[]
  >([]);
  top3$: Observable<Top3 | null>;
  comments$: BehaviorSubject<Comment[]> = new BehaviorSubject<Comment[]>([]);
  currentUser: User;
  subscriptions: Subscription[] = [];
  gpsOnlyId: number;

  loadTrainingSectionAndCoordinates(id: number): void {
    const combinedObsservables: Observable<{
      coordinates: Coordinate[];
      trainingSections: TrainingSection[];
    }> = forkJoin({
      coordinates: this.gpsOnlyService.getCoordinates(id),
      trainingSections: this.gpsOnlyService.getTrainingSections(id),
    });
    this.subscriptions.push(
      combinedObsservables.subscribe(
        (result: {
          coordinates: Coordinate[];
          trainingSections: TrainingSection[];
        }) => {
          this.coordinates$.next(result.coordinates);
          this.trainingSections$.next(result.trainingSections);
        }
      )
    );
  }

  onLikeClick(gpsOnly: GpsOnly): void {
    const likeAction: Observable<{ data: { likes_count: number } }> =
      gpsOnly.has_liked
        ? this.gpsOnlyService.deleteLike(this.gpsOnlyId)
        : this.gpsOnlyService.postLike(this.gpsOnlyId);
    this.subscriptions.push(
      likeAction.subscribe(({ data: { likes_count } }) => {
        gpsOnly.likes_count = likes_count;
        this.information$ = of(gpsOnly);
      })
    );
  }

  onDownloadGPX(gpsOnlyId: number): void {
    this.subscriptions.push(
      this.gpsOnlyService
        .getMapmodeGPX(gpsOnlyId)
        .subscribe((blob: Blob) =>
          this.watchActionsService.downloadGPX(gpsOnlyId, blob)
        )
    );
  }

  getComments(): void {
    this.subscriptions.push(
      this.gpsOnlyService
        .getComments(this.gpsOnlyId)
        .subscribe((comments: Comment[]) => this.comments$.next(comments))
    );
  }

  getTop3(): void {
    this.top3$ = this.activityApi
      .getMapModeActivityTop(+this.gpsOnlyId)
      .pipe(shareReplay(1));
  }

  editGpsOnly(formData: any): void {}

  ngOnInit(): void {
    this.gpsOnlyId = this.route.snapshot.params['gpsOnlyId'];
    this.information$ = this.gpsOnlyService
      .getInformation(this.gpsOnlyId)
      .pipe(shareReplay(1));
    this.loadTrainingSectionAndCoordinates(this.gpsOnlyId);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
}
